import React from 'react';
import { motion } from 'framer-motion';
import { COMPANY, addressOneLine } from '../data/company';

const PrivacyPromptWick = () => {
  return (
    <div className="pt-24 pb-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 legal-content">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <span className="text-accent-amber font-semibold tracking-wider text-sm uppercase mb-2 block">App Specific Policy</span>
        <h1>PromptWick Privacy Policy</h1>
        <p className="text-sm border-l-2 border-accent-amber pl-4 mb-8">
          Last Updated: September 2026
        </p>

        <p>
          This document covers <strong>PromptWick</strong>, our Chrome extension, and is an addendum
          to the general MediaVerse Labs Privacy Policy. PromptWick differs from our desktop
          software in one important way: the rewrite happens on a server, so some of what you type
          does leave your browser. This page says exactly what, and when.
        </p>

        <h2>1. Who is responsible for your data</h2>
        <p>
          The data controller for PromptWick is <strong>{COMPANY.legalName}</strong>, a company
          registered in {COMPANY.jurisdiction} under company number <strong>{COMPANY.number}</strong>,
          registered office {addressOneLine}. {COMPANY.tradingName} is a trading name of that company.
        </p>

        <h2>2. What we receive, and when</h2>
        <p>
          Nothing is sent anywhere until you press <strong>Improve</strong>. When you do, we receive:
        </p>
        <ul>
          <li>The prompt text you asked us to improve, and the action, tone and type you selected.</li>
          <li>An identifier: either an anonymous device id generated in your browser, or your account id if you are signed in.</li>
        </ul>
        <p>
          The extension does not read the rest of the page, your browsing history, your other tabs,
          or anything you type outside the prompt box.
        </p>

        <h2>3. Where your prompt goes</h2>
        <p>
          Your prompt is sent to our backend, which passes it to the third-party language-model
          provider that produces the rewrite, and returns the result to your browser. We do not sell
          your prompts, use them for advertising, or share them with anyone other than the provider
          performing the rewrite.
        </p>

        <h2>4. What we store</h2>
        <ul>
          <li><strong>On your device</strong> (Chrome local storage): your device id, your session tokens if you sign in, and your interface preferences.</li>
          <li><strong>On our servers</strong>: your prompt history, your saved library entries, your variables, and your plan and usage counters.</li>
        </ul>

        <h2>5. Accounts are optional</h2>
        <p>
          The free plan works without an account, using an anonymous device id. If you choose to sign
          in with Google, we receive your email address and use it only to identify your account and
          apply your plan. Sign-in is handled by our backend, so the OAuth client secret never ships
          inside the extension. When you sign in, anything you saved anonymously on that device is
          claimed into your account so it is not stranded.
        </p>

        <h2>6. Permissions, and why each one exists</h2>
        <ul>
          <li><code>storage</code> — to keep your device id, session and preferences in the browser.</li>
          <li><code>sidePanel</code> — to open the panel the extension runs in.</li>
          <li><code>identity</code> — only to run the Google sign-in flow when you ask for it.</li>
          <li><strong>Site access</strong> — limited to the eleven AI chat sites the extension supports, so the button can appear in their prompt boxes and insert the improved text back. It requests no access to any other site.</li>
        </ul>

        <h2>7. Payments</h2>
        <p>
          Paid plans are processed by Stripe. Card details are handled by Stripe and never reach our
          servers or the extension. We receive only your subscription status.
        </p>

        <h2>8. No ads, no third-party analytics</h2>
        <p>
          The extension contains no advertising code, no third-party analytics and no remotely loaded
          scripts. It runs only the code shipped in the reviewed package.
        </p>

        <h2>9. Deleting your data</h2>
        <p>
          You can delete your account and its stored prompts from within the side panel, under
          Settings. Removing the extension clears everything held in your browser. To request
          deletion by email, write to the address below.
        </p>

        <h2>10. Contact</h2>
        <p>
          For privacy inquiries: <a href="mailto:hello@mediaverselabs.com">hello@mediaverselabs.com</a>
        </p>
      </motion.div>
    </div>
  );
};

export default PrivacyPromptWick;
